// src/components/workloads/creation/CsvUploadHandler.tsx
import React, { useState, useCallback, useRef } from 'react';
import {
  Upload,
  File,
  X,
  AlertTriangle,
  Check,
  ChevronDown,
  Loader2,
  MapPin,
} from 'lucide-react';
import { useWorkloadStore } from '../../../store/workloadStore';
import {
  csvService,
  CsvParseResult,
  ColumnMapping,
  ValidationResult,
} from '../../../services/csv.service';
import { geocodingService } from '../../../services/geocoding.service';

const CsvUploadHandler: React.FC = () => {
  const {
    creation,
    setCreationCsvFile,
    setCreationCsvMapping,
    setCreationAddresses,
  } = useWorkloadStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState({ current: 0, total: 0 });
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload');

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      handleFile(file);
    }
  }, []);

  // Handle file selection
  const handleFile = useCallback(async (file: File) => {
    setIsProcessing(true);
    setCreationCsvFile(file);

    try {
      const result = await csvService.parse(file, { preview: 100 });
      setParseResult(result);

      // Auto-detect column mapping
      const detectedMapping = csvService.detectColumnMapping(result.headers);
      setMapping(detectedMapping);

      setStep('mapping');
    } catch (error: any) {
      console.error('Error parsing CSV:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [setCreationCsvFile]);

  // Handle mapping change
  const handleMappingChange = useCallback((field: keyof ColumnMapping, value: string | null) => {
    if (!mapping) return;

    const newMapping = { ...mapping };
    if (field === 'tags') {
      // Tags is an array
      if (value) {
        newMapping.tags = [...mapping.tags, value];
      }
    } else {
      (newMapping as any)[field] = value;
    }

    setMapping(newMapping);
    setCreationCsvMapping(newMapping as any);
  }, [mapping, setCreationCsvMapping]);

  // Validate and process
  const handleValidate = useCallback(async () => {
    if (!parseResult || !mapping) return;

    setIsProcessing(true);

    try {
      // Validate data
      const validationResult = csvService.validateData(parseResult.data, mapping);
      setValidation(validationResult);

      if (validationResult.validRows > 0) {
        setStep('preview');

        // Start geocoding valid rows
        setIsGeocoding(true);
        setGeocodingProgress({ current: 0, total: validationResult.validRows });

        const validAddresses = validationResult.data.filter((row) => row.isValid);
        const geocodedAddresses: typeof creation.addresses = [];

        for (let i = 0; i < validAddresses.length; i++) {
          const row = validAddresses[i];

          try {
            const geocoded = await geocodingService.geocodeAddress(row.fullAddress);
            geocodedAddresses.push({
              street: row.street,
              unit: row.unit,
              city: row.city,
              state: row.state,
              zipCode: row.zipCode,
              location: geocoded.location,
              selected: true,
            });
          } catch {
            // Skip failed geocodes
            console.warn(`Failed to geocode: ${row.fullAddress}`);
          }

          setGeocodingProgress({ current: i + 1, total: validAddresses.length });
        }

        setCreationAddresses(geocodedAddresses);
        setIsGeocoding(false);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [parseResult, mapping, creation.addresses, setCreationAddresses]);

  // Column select component
  const ColumnSelect: React.FC<{
    label: string;
    value: string | null;
    field: keyof Omit<ColumnMapping, 'tags'>;
    required?: boolean;
  }> = ({ label, value, field, required }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        value={value || ''}
        onChange={(e) => handleMappingChange(field, e.target.value || null)}
        className={`w-full rounded-md border px-3 py-2 text-sm ${
          required && !value
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
        }`}
      >
        <option value="">-- Select column --</option>
        {parseResult?.headers.map((header) => (
          <option key={header} value={header}>
            {header}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Upload Your Address List
        </h2>
        <p className="text-gray-600">
          Import addresses from a CSV file. We'll help you map the columns and validate the data.
        </p>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-indigo-400 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden"
          />

          {isProcessing ? (
            <div>
              <Loader2 className="h-12 w-12 text-indigo-600 mx-auto animate-spin" />
              <p className="mt-4 text-gray-600">Processing file...</p>
            </div>
          ) : (
            <>
              <Upload className="h-12 w-12 text-gray-400 mx-auto" />
              <p className="mt-4 text-gray-600">
                Drag and drop your CSV file here, or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  browse
                </button>
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Supports CSV files up to 10MB
              </p>
            </>
          )}
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && parseResult && mapping && (
        <div>
          {/* File info */}
          <div className="flex items-center bg-gray-50 rounded-lg p-4 mb-6">
            <File className="h-8 w-8 text-gray-400 mr-3" />
            <div className="flex-1">
              <p className="font-medium text-gray-900">{creation.csvFile?.name}</p>
              <p className="text-sm text-gray-500">{parseResult.rowCount} rows found</p>
            </div>
            <button
              onClick={() => {
                setParseResult(null);
                setMapping(null);
                setStep('upload');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Column mapping */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-medium text-gray-900 mb-4">Map Your Columns</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <ColumnSelect
                label="Street Address"
                value={mapping.street}
                field="street"
                required
              />
              <ColumnSelect
                label="Unit/Apt (optional)"
                value={mapping.unit}
                field="unit"
              />
              <ColumnSelect
                label="City"
                value={mapping.city}
                field="city"
                required
              />
              <ColumnSelect
                label="State"
                value={mapping.state}
                field="state"
                required
              />
              <ColumnSelect
                label="ZIP Code"
                value={mapping.zipCode}
                field="zipCode"
                required
              />
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Optional Fields</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <ColumnSelect
                  label="First Name"
                  value={mapping.firstName}
                  field="firstName"
                />
                <ColumnSelect
                  label="Last Name"
                  value={mapping.lastName}
                  field="lastName"
                />
                <ColumnSelect
                  label="Phone"
                  value={mapping.phone}
                  field="phone"
                />
                <ColumnSelect
                  label="Email"
                  value={mapping.email}
                  field="email"
                />
                <ColumnSelect
                  label="Notes"
                  value={mapping.notes}
                  field="notes"
                />
              </div>
            </div>

            {/* Tag columns */}
            {mapping.tags.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">
                  Tag Columns (will be imported as address tags)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {mapping.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Validate button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleValidate}
                disabled={
                  !mapping.street || !mapping.city || !mapping.state || !mapping.zipCode
                }
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Validate & Process
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && validation && (
        <div>
          {/* Summary */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <span className="font-medium text-green-800">Valid Rows</span>
              </div>
              <p className="text-2xl font-bold text-green-900 mt-2">
                {validation.validRows}
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                <span className="font-medium text-red-800">Invalid Rows</span>
              </div>
              <p className="text-2xl font-bold text-red-900 mt-2">
                {validation.invalidRows}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-blue-500 mr-2" />
                <span className="font-medium text-blue-800">Geocoded</span>
              </div>
              {isGeocoding ? (
                <div className="mt-2">
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-blue-900">
                      {geocodingProgress.current} / {geocodingProgress.total}
                    </span>
                  </div>
                  <div className="mt-1 bg-blue-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full transition-all"
                      style={{
                        width: `${(geocodingProgress.current / geocodingProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-2xl font-bold text-blue-900 mt-2">
                  {creation.addresses.length}
                </p>
              )}
            </div>
          </div>

          {/* Errors list */}
          {validation.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-red-800 mb-2">
                Validation Errors ({validation.errors.length})
              </h4>
              <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                {validation.errors.slice(0, 10).map((error, i) => (
                  <li key={i}>
                    Row {error.row}: {error.message}
                  </li>
                ))}
                {validation.errors.length > 10 && (
                  <li className="text-red-600">
                    ...and {validation.errors.length - 10} more errors
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Preview table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Address
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      City
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      State
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      ZIP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {validation.data.slice(0, 10).map((row) => (
                    <tr
                      key={row.rowIndex}
                      className={row.isValid ? '' : 'bg-red-50'}
                    >
                      <td className="px-4 py-3 text-sm">
                        {row.isValid ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {row.street}
                        {row.unit && ` Unit ${row.unit}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.city}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.state}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.zipCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {validation.data.length > 10 && (
              <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600 text-center">
                Showing 10 of {validation.data.length} rows
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvUploadHandler;
